import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { localeDate } from "../../../../app/datetimeUtils";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import TextWithTip from "../../../../components/TextWithTip";
import { formatNumber, formatNumberOneDecimal } from "../../shared/reportUtil";
import { AdminReportsResponse, ReportLevel } from "../adminReportsApiSlice";

const tooltipOptions: TooltipOptions = {
	position: "top",
};

// Required properties for the table
interface PromptTableRequiredBase {
	essayCount: number;
	draftCount: number;
	averageNumberOfDraftsPerEssay: number;
	averageDevelopment: number;
	averageOrganization: number;
	averageStyle: number;
	averageWordChoice: number;
	averageSentenceStructure: number;
	averageConventions: number;
	averageScore: number;
}

interface PromptTableProps<T extends PromptTableRequiredBase> {
	className: string;
	level: ReportLevel;
	data: AdminReportsResponse<T>;
	hideRows: boolean;
	dataKey: keyof T;
	nameField: keyof T;
	nameHeader: string;
	onNameClick?: (row: T) => void;
	showTeacherColumn?: boolean;
	teacherField?: keyof T;
	showMostRecentColumn?: boolean;
	mostRecentField?: keyof T;
}

export function PromptTable<T extends PromptTableRequiredBase>(props: PromptTableProps<T>) {
	const hasData = props.data && props.data.rows.length > 0;

	const nameTemplate = (rowData: T) => {
		const isClickable = props.onNameClick !== undefined;
		return (
			<a className={isClickable ? "link" : ""} onClick={() => props.onNameClick?.(rowData)}>
				{rowData[props.nameField] as string}
			</a>
		);
	};

	const teacherTemplate = (rowData: T) => {
		if (!props.teacherField) return null;
		const teachers = rowData[props.teacherField] as string;
		return (
			<span>
				<TextWithTip uniqueName={`teachers-${rowData[props.dataKey]}`} text={teachers} />
			</span>
		);
	};

	const submissionTemplate = (rowData: T) => {
		if (!props.mostRecentField) return null;
		const date = rowData[props.mostRecentField] as Date;
		return <span>{localeDate(date)}</span>;
	};

	const essayCountTemplate = (rowData: T) => {
		return formatNumber(rowData.essayCount);
	};

	const draftCountTemplate = (rowData: T) => {
		return formatNumber(rowData.draftCount);
	};

	const draftsPerEssayTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageNumberOfDraftsPerEssay);
	};

	const developmentTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageDevelopment);
	};

	const organizationTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageOrganization);
	};

	const styleTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageStyle);
	};

	const wordChoiceTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageWordChoice);
	};

	const sentenceStructureTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageSentenceStructure);
	};

	const conventionsTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageConventions);
	};

	const averageTemplate = (rowData: T) => {
		return formatNumberOneDecimal(rowData.averageScore);
	};

	// Calculate footer colspan based on visible columns
	const getFooterColSpan = () => {
		let span = 1; // Start with name column
		if (props.showTeacherColumn) span++;
		if (props.showMostRecentColumn) span++;
		return span;
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column
					footer={`${props.level}-Wide`}
					footerClassName="font-medium !text-right !px-2"
					colSpan={getFooterColSpan()}
				/>
				<Column
					footer={formatNumber(props.data.totals.essayCount)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumber(props.data.totals.draftCount)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageNumberOfDraftsPerEssay)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageDevelopment)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageOrganization)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageStyle)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageWordChoice)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageSentenceStructure)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageConventions)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageScore)}
					footerClassName="font-medium !px-2"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.hideRows ? [] : props.data.rows}
			dataKey={props.dataKey as string}
			loading={props.data.rows == undefined}
			emptyMessage="No results found"
			pt={{
				// Targets the container of the "No results found" message for hiding it
				emptyMessage: {
					style: { display: !props.hideRows ? "table-row" : "none" },
				},
			}}
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column
				header={props.nameHeader}
				headerClassName="!px-2"
				className="!px-2"
				body={nameTemplate}
				sortable
				sortField={props.nameField as string}></Column>
			{props.showTeacherColumn && props.teacherField && (
				<Column
					header="Teacher(s)"
					headerClassName="!px-2"
					className="!px-2 !max-w-52"
					body={teacherTemplate}
					sortable
					sortField={props.teacherField as string}></Column>
			)}
			{props.showMostRecentColumn && props.mostRecentField && (
				<Column
					header="Most Recent Draft"
					headerClassName="!px-2"
					className="!px-2"
					body={submissionTemplate}
					sortable
					sortField={props.mostRecentField as string}></Column>
			)}
			<Column
				header="Number of Essays"
				headerClassName="!px-2"
				className="!px-2"
				body={essayCountTemplate}
				sortable
				sortField="essayCount"></Column>
			<Column
				header="Number of Drafts"
				headerClassName="!px-2"
				className="!px-2"
				body={draftCountTemplate}
				sortable
				sortField="draftCount"></Column>
			<Column
				header="Avg Drafts Per Essay"
				headerClassName="!px-2"
				className="!px-2"
				body={draftsPerEssayTemplate}
				sortable
				sortField="averageNumberOfDraftsPerEssay"></Column>
			<Column
				header="Dev"
				headerTooltip="Development of Ideas"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={developmentTemplate}
				sortable
				sortField="averageDevelopment"></Column>
			<Column
				header="Org"
				headerTooltip="Organization"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={organizationTemplate}
				sortable
				sortField="averageOrganization"></Column>
			<Column
				header="Style"
				headerTooltip="Style"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={styleTemplate}
				sortable
				sortField="averageStyle"></Column>
			<Column
				header="WC"
				headerTooltip="Word Choice"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={wordChoiceTemplate}
				sortable
				sortField="averageWordChoice"></Column>
			<Column
				header="Sent"
				headerTooltip="Sentence Fluency"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={sentenceStructureTemplate}
				sortable
				sortField="averageSentenceStructure"></Column>
			<Column
				header="Conv"
				headerTooltip="Conventions"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={conventionsTemplate}
				sortable
				sortField="averageConventions"></Column>
			<Column
				header="Total"
				headerClassName="!px-2"
				className="!px-2"
				body={averageTemplate}
				sortable
				sortField="averageScore"></Column>
		</DataTable>
	);
}
